import React, { ChangeEvent, useState } from "react";
import { InfinityListComponent } from "../../../../shared/components/infinity-list.component";
import { PrimaryButton } from "../../../../components/Buttons/PrimaryButton";
import { SecondaryButton } from "../../../../components/Buttons/SecondaryButton";
import { extensionsToMimeTypesString } from "../../../logo-editor/componenets/ExtensionsToMimeTypesString";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileArrowDown,
  faUpload,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import {
  BaseLogo,
  LogoType,
} from "../../../../../core/models/entities/BaseLogo";
import { LogoOptions } from "../../../../../core/models/entities/LogoOptions";
import { LogoCardLibrary } from "../cards/logo-card-library.component";
import TableSearchBar from "../../../../components/GenericTable/SubComponents/TableHeader/TableSearchBar";
import Dropdown from "../../../../components/dropdown/Dropdown";

export interface LibrarySidePanel_VM {
  onBack: () => void;
  isLoading: boolean;
  onLoadMore: () => void;
  logos: BaseLogo[];
  onUpload: (file: File) => void;
  onAddLogo: (logo: BaseLogo) => void;
  onDeleteLogo: (logo: BaseLogo) => void;
  options: LogoOptions;
  onFilter: (term: string) => void;
  term: string;
  onTypeFilter: (typeFilter: string | null) => void;
  typeFilter: string | null;
  onLogoTypeFilter: (logoTypeFilter: string | null) => void;
  logoTypeFilter: string | null;
}

export function LibrarySidePanel({
  onAddLogo,
  onDeleteLogo,
  logos,
  onUpload,
  onLoadMore,
  isLoading,
  onBack,
  options,
  onFilter,
  term,
  onTypeFilter,
  typeFilter,
  onLogoTypeFilter,
  logoTypeFilter,
}: LibrarySidePanel_VM) {
  const [isDragging, setIsDragging] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [openFilterId, setOpenFilterId] = useState<string | undefined>(
    undefined
  );
  const logoClassifications = [
    { value: 3, name: "Logo" },
    { value: 2, name: "Name" },
    { value: 6, name: "Numberset" },
    { value: 4, name: "Club Logo" },
    { value: 11, name: "Chain Logo" },
  ];

  const hasSearchTerm = term.trim().length > 0;
  const hasFilters = Boolean(typeFilter || logoTypeFilter);
  const emptyStateCopy =
    hasSearchTerm || hasFilters
      ? "No logos match the current search or filters. Try adjusting your filters or clearing the search."
      : "This customer has no saved logos yet. Upload a file or create a new logo to populate their library.";

  function handleDragCatalog(
    e: React.DragEvent<HTMLDivElement | HTMLFormElement>
  ) {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setIsDragging(true);
    } else if (e.type === "dragleave") {
      setIsDragging(false);
    }
  }

  function handleDropCatalog(
    e: React.DragEvent<HTMLDivElement | HTMLFormElement>
  ) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      onUpload(e.dataTransfer.files[0]);
    }
  }

  function handleSelectCatalog(event: ChangeEvent<HTMLInputElement>) {
    event.stopPropagation();
    event.preventDefault();

    const files = event.target.files;

    if (files && files.length > 0) {
      const file = files[0];
      onUpload(file);
    }
  }

  const inputRef = React.useRef<HTMLInputElement>(null);

  function onUploadClick() {
    inputRef.current?.click();
  }

  return (
    <div
      className={"relative space-y-2 flex flex-col h-full py-4"}
      onDragEnter={handleDragCatalog}
    >
      <p className={"text-sm font-semibold"}>Add Logo to Design</p>
      <div className="flex justify-between w-full items-center space-x-2 pb-2">
        <TableSearchBar
          onSearchQueryChange={(x) => onFilter(x.searchTerm)}
          searchQuery={term}
          border={true}
        />
        <div className="relative">
          <button
            className="px-3 py-2 text-sm border rounded-md flex items-center gap-2 hover:bg-gray-50"
            onClick={() => setFiltersOpen((s) => !s)}
          >
            Filters
            {(typeFilter ? 1 : 0) + (logoTypeFilter ? 1 : 0) > 0 && (
              <span className="ml-1 inline-flex items-center justify-center text-xs bg-gray-200 rounded-full w-5 h-5">
                {(typeFilter ? 1 : 0) + (logoTypeFilter ? 1 : 0)}
              </span>
            )}
          </button>
          {filtersOpen && (
            <div className="absolute mt-2 bg-white border rounded-md shadow-lg z-20 p-3 space-y-3">
              <div className="relative">
                <Dropdown
                  selectedOption={
                    typeFilter
                      ? options.transferLogoTypes.find(
                          (t) => t.typeKey === typeFilter
                        )
                      : undefined
                  }
                  onOptionSelect={(option) => {
                    onTypeFilter(option ? option.typeKey : null);
                    setFiltersOpen(false);
                  }}
                  options={options.transferLogoTypes}
                  getOptionDisplay={(option) => option.nickname.toLowerCase()}
                  defaultTitle="Type"
                  width="large"
                  open={openFilterId === "type"}
                  onOpenToggle={(open) =>
                    setOpenFilterId(open ? "type" : undefined)
                  }
                />
                {typeFilter && (
                  <button
                    className="absolute -top-1 -right-1 w-3 h-3 bg-gray-500 rounded-full flex items-center justify-center text-white hover:bg-red-500 transition-colors z-0"
                    onClick={() => onTypeFilter(null)}
                    title="Remove Type filter"
                  >
                    <FontAwesomeIcon icon={faTimes} className="text-xs" />
                  </button>
                )}
              </div>
              <div className="relative">
                <Dropdown
                  selectedOption={
                    logoTypeFilter
                      ? logoClassifications.find(
                          (c) => c.value.toString() === logoTypeFilter
                        )
                      : undefined
                  }
                  onOptionSelect={(option) => {
                    onLogoTypeFilter(option ? option.value.toString() : null);
                    setFiltersOpen(false);
                  }}
                  options={logoClassifications}
                  getOptionDisplay={(option) => option.name.toLowerCase()}
                  defaultTitle="Logo Type"
                  width="large"
                  open={openFilterId === "classification"}
                  onOpenToggle={(open) =>
                    setOpenFilterId(open ? "classification" : undefined)
                  }
                />
                {logoTypeFilter && (
                  <button
                    className="absolute -top-1 -right-1 w-3 h-3 bg-gray-500 rounded-full flex items-center justify-center text-white hover:bg-red-500 transition-colors z-0"
                    onClick={() => onLogoTypeFilter(null)}
                    title="Remove Logo Type filter"
                  >
                    <FontAwesomeIcon icon={faTimes} className="text-xs" />
                  </button>
                )}
              </div>
              <div className="flex">
                <button
                  className="text-xs text-gray-600 hover:text-gray-900"
                  onClick={() => setFiltersOpen(false)}
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* active filter chips removed in favor of inline clear icons */}
      <InfinityListComponent
        className={"flex-1 !max-h-[680px]"}
        isLoading={isLoading}
        currentItems={logos}
        onLoadMore={onLoadMore}
        emptyState={
          <p className="text-sm text-gray-500 leading-relaxed px-6">
            {emptyStateCopy}
          </p>
        }
        renderItem={(logo) => (
          <LogoCardLibrary
            onAdd={() => onAddLogo(logo)}
            onDelete={() => onDeleteLogo(logo)}
            key={logo.id}
            logo={logo}
          />
        )}
      />
      <PrimaryButton
        width={"full"}
        className="flex items-center gap-x-2 px-4"
        onClick={onUploadClick}
      >
        <FontAwesomeIcon icon={faUpload} />
        Upload new File
      </PrimaryButton>
      <SecondaryButton width={"full"} onClick={onBack}>
        Back
      </SecondaryButton>
      <input
        ref={inputRef}
        className={"hidden"}
        type="file"
        id="input-file-upload"
        onChange={handleSelectCatalog}
        accept={extensionsToMimeTypesString(options.extensions)}
      />
      {isDragging && (
        <>
          <div
            className={
              "z-10 absolute w-full h-full top-0 bottom-0 left-0 right-0"
            }
            onDragEnter={handleDragCatalog}
            onDragLeave={handleDragCatalog}
            onDragOver={handleDragCatalog}
            onDrop={handleDropCatalog}
          ></div>
          <div
            id={"drop-overlay"}
            className={
              "animate-fadeInFast bg-gray-200 absolute w-full h-full top-0 bottom-0 left-0 right-0 flex justify-center items-center"
            }
          >
            <div className={"space-y-3 flex flex-col h-5 mt-[-60px]"}>
              <FontAwesomeIcon
                icon={faFileArrowDown}
                size={"2x"}
                className={"mx-auto "}
              />
              <p className={"italic font-light text-xs"}>Drop image here</p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
